import I18n from 'i18n-js';
import React from 'react';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import FormGroupWithRadio from '@reverbdotcom/commons/src/components/form_group_with_radio';
import { RCTag } from '@reverbdotcom/commons/src/rc/RCTag';
import SiteModule from '@reverbdotcom/commons/src/components/site_module';
import { EVERYWHERE_CODE } from '@reverbdotcom/commons/src/constants';

import EditShippingRateCardManualPrices, { IShippingRate } from './edit_shipping_rate_card_manual_prices';
import URLHelpers from './url_helpers';

export enum FormContext {
  SHIPPING_PROFILE = 'shippingProfile',
  LISTING = 'listing',
}

enum ShippingMode {
  CUSTOM = 'custom',
  CARRIER_CALCULATED = 'carrierCalculated',
}

const CARRIER_CALC_MAX_LENGTH_INCHES = 22;
const CARRIER_CALC_MAX_WEIGHT_POUNDS = 10;

// This component currently renders inputs within haml forms, and uses input names expected by shipping profile/listing rails controllers.
// Once shipping rates can be submitted via a mutation, this naming can be stripped out/simplified.
export const INPUT_NAME_PREFIXES = {
  [FormContext.SHIPPING_PROFILE]: 'shipping_profile[shipping][rates][]',
  [FormContext.LISTING]: 'product[shipping][rates][]',
};

function RadioLabel({ shippingMode }: { shippingMode: ShippingMode }) {
  const forCarrierCalculated = shippingMode === 'carrierCalculated';
  return (
    <label htmlFor={`${shippingMode}ShippingRadio`}>
      <span className="mr-1 size-120 weight-normal">
        {I18n.t(`discovery.dashboard.selling.shippingPackageType.${shippingMode}.title`)}

        {forCarrierCalculated && (
          <span className="sell-form-package-inputs-container__title-emphasize">
            {I18n.t(`discovery.dashboard.selling.shippingPackageType.${shippingMode}.carrierSpecification`)}
          </span>
        )}
      </span>
      <RCTag variant="highlight">
        {I18n.t(`discovery.dashboard.selling.shippingPackageType.${shippingMode}.badgeText`)}
      </RCTag>
    </label>
  );
}

function RadioHelpText({ shippingMode, shopAddressSummary }: { shippingMode: ShippingMode; shopAddressSummary?: string; }) {
  const forCarrierCalculated = shippingMode === 'carrierCalculated';

  return (
    <div>
      <p className="sell-form-package-inputs-container__help-text">
        {forCarrierCalculated && (
          <Translate
            text="discovery.dashboard.selling.shippingPackageType.carrierCalculated.helpText"
            args={{ shopAddressSummary }}
          >
            <CoreLink to={URLHelpers.shopEditPath({ anchor: 'edit_shop_location' })} target="_blank">
              {I18n.t('discovery.dashboard.selling.shippingPackageType.carrierCalculated.shopAddress')}
            </CoreLink>
          </Translate>
        )}

        {!forCarrierCalculated && I18n.t('discovery.dashboard.selling.shippingRates.manualFieldsSubtitle')}
      </p>
    </div>
  );
}

function CarrierCalculatedDescription() {
  return (
    <ul className="sell-form-package-inputs-container__list-group">
      <li className="sell-form-package-inputs-container__list-item">
        {I18n.t('discovery.dashboard.selling.shippingPackageType.carrierCalculated.description.dimensionLimits', {
          maxLengthInches: String(CARRIER_CALC_MAX_LENGTH_INCHES),
          maxWeightPounds: String(CARRIER_CALC_MAX_WEIGHT_POUNDS),
        })}
      </li>

      <li className="sell-form-package-inputs-container__list-item">
        {I18n.t('discovery.dashboard.selling.shippingPackageType.carrierCalculated.description.willSendLabel')}
      </li>

      <li className="sell-form-package-inputs-container__list-item">
        <Translate
          text="discovery.dashboard.selling.shippingPackageType.carrierCalculated.description.bySelecting"
        >
          <CoreLink to={URLHelpers.legalPagePath('calculated-shipping-terms-and-conditions')} target="_blank">
            {I18n.t('discovery.dashboard.selling.shippingPackageType.carrierCalculated.description.terms')}
          </CoreLink>
        </Translate>
      </li>
    </ul>
  );
}

function EditShippingRateCardPackageInputs() {
  return (<div>Package inputs coming soon</div>);
}

interface IProps {
  shippingRate?: IShippingRate;
  formContext: FormContext;
  currency: string;
  handleRemoveShippingRate?: (IShippingRate) => void;
  handleEditShippingRate?: (IShippingRate) => void;
  allowedToShipCarrierCalculated: boolean;
  inCarrierCalculatedMode: boolean;
  allowedToShipExpedited: boolean;
  setIsInCarrierCalculatedMode: (boolean) => void;
  shopAddressSummary?: string;
}

export function EditShippingRateCard({
  shippingRate,
  allowedToShipExpedited,
  handleRemoveShippingRate,
  handleEditShippingRate,
  formContext,
  currency,
  allowedToShipCarrierCalculated,
  inCarrierCalculatedMode,
  setIsInCarrierCalculatedMode,
  shopAddressSummary,
}: IProps) {
  const isEverywhereElseRegion = shippingRate.regionCode === EVERYWHERE_CODE;

  return (
    <div className="shipping-rate-card">
      <header className="shipping-rate-card__header">
        <div className="shipping-rate-card__titleblock">
          <h4 className="shipping-rate-card__title">
            {isEverywhereElseRegion && (
              <span className="icon-l-globe">
                {I18n.t('discovery.dashboard.selling.shippingRates.everywhereElse')}
              </span>)}

            {!isEverywhereElseRegion && shippingRate.regionName}
          </h4>
          {isEverywhereElseRegion &&
            <p className="shipping-rate-card__subtitle">
              {I18n.t('discovery.dashboard.selling.shippingRates.everywhereElseTip')}
            </p>
          }
        </div>
        <div className="shipping-rate-card__remove">
          {shippingRate.isRequired && (<span className="form-group__tag">{I18n.t('commons.formGroup.required')}</span>)}

          {!shippingRate.isRequired && (
            <button
              className="d-block button-as-link size-80 color-red"
              onClick={() => handleRemoveShippingRate(shippingRate)}
              type="button"
            >
              {I18n.t('discovery.dashboard.selling.shippingRates.rateFields.removeRate')}
            </button>
          )}
        </div>
      </header>
      <div className="shipping-rate-card__main">
        <input
          type="hidden"
          name={`${INPUT_NAME_PREFIXES[formContext]}[region_type]`}
          value={shippingRate.regionType}
          data-region-type="true"
        />
        <input
          type="hidden"
          name={`${INPUT_NAME_PREFIXES[formContext]}[region_code]`}
          value={shippingRate.regionCode}
          data-region-code="true"
        />

        {allowedToShipCarrierCalculated && (
          <>
            <p><strong>{I18n.t('discovery.dashboard.selling.shippingPackageType.header')}</strong></p>
            <SiteModule footerContent={<CarrierCalculatedDescription />}>
              <FormGroupWithRadio
                inputName={`carrier_calculated_shipping_radio_${shippingRate.regionCode}`}
                label={<RadioLabel shippingMode={ShippingMode.CARRIER_CALCULATED} />}
                helpText={<RadioHelpText shippingMode={ShippingMode.CARRIER_CALCULATED} shopAddressSummary={shopAddressSummary} />}
                value={`${inCarrierCalculatedMode}`}
                checked={inCarrierCalculatedMode}
                updateField={() => setIsInCarrierCalculatedMode(true)}
              />

              {inCarrierCalculatedMode && (<EditShippingRateCardPackageInputs />)}
            </SiteModule>

            <SiteModule>
              <FormGroupWithRadio
                inputName={`custom_shipping_radio_${shippingRate.regionCode}`}
                label={<RadioLabel shippingMode={ShippingMode.CUSTOM} />}
                helpText={<RadioHelpText shippingMode={ShippingMode.CUSTOM} />}
                value={`${!inCarrierCalculatedMode}`}
                checked={!inCarrierCalculatedMode}
                updateField={() => setIsInCarrierCalculatedMode(false)}
              />

              {!inCarrierCalculatedMode && (
                <EditShippingRateCardManualPrices
                  formContext={formContext}
                  shippingRate={shippingRate}
                  handleEditShippingRate={handleEditShippingRate}
                  currency={currency}
                  allowedToShipExpedited={allowedToShipExpedited}
                />
              )}
            </SiteModule>
          </>
        )}

        {!allowedToShipCarrierCalculated && (
          <SiteModule>
            <p className="shipping-rate-card__help__title">{I18n.t('discovery.dashboard.selling.shippingRates.manualFieldsTitle')}</p>

            <p className="shipping-rate-card__help__subtitle">{I18n.t('discovery.dashboard.selling.shippingRates.manualFieldsSubtitle')}</p>

            <EditShippingRateCardManualPrices
              formContext={formContext}
              shippingRate={shippingRate}
              handleEditShippingRate={handleEditShippingRate}
              currency={currency}
              allowedToShipExpedited={allowedToShipExpedited}
            />
          </SiteModule>
        )}
      </div>
    </div>
  );
}

export default EditShippingRateCard;
